/**
 * SaveTemplatesPromptModal
 *
 * Surfaced when a contractor clicks Save on an estimate that contains one or
 * more template-derived zones (zones with `sourceTemplateId`) whose line items
 * have diverged from the original global template. Per-zone, the contractor
 * can toggle "save as private template" and name it. Defaults to ON with the
 * zone name pre-filled, so the path of least resistance saves a reusable
 * template under the contractor's own naming.
 */

import React, { useEffect, useState } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useIdioma } from '../../i18n/idioma';
import { X, BookmarkPlus, Pencil } from 'lucide-react';
import { Button } from '../ui/Button';

export interface PromptableZone {
  zoneId: string;
  zoneName: string;
  sourceTemplateId: string;
  sourceTemplateName: string;
  itemCount: number;
  changedLineCount: number;
  /** The current items in this zone — passed back so the caller can persist. */
  items: any[];
}

export interface PrivateTemplateSaveDecision {
  zoneId: string;
  sourceTemplateId: string;
  /** Final name the contractor chose, only meaningful when `save` is true. */
  name: string;
  save: boolean;
  items: any[];
}

interface SaveTemplatesPromptModalProps {
  zones: PromptableZone[];
  onConfirm: (decisions: PrivateTemplateSaveDecision[]) => void;
  onSkip: () => void;
  onClose: () => void;
}

const SaveTemplatesPromptModal: React.FC<SaveTemplatesPromptModalProps> = ({ zones, onConfirm, onSkip, onClose }) => {
    const { t } = useIdioma();
  // Per-zone form state. Default: save=ON, name pre-filled from zone name.
  const [decisions, setDecisions] = useState<Record<string, { name: string; save: boolean }>>(
    () => Object.fromEntries(zones.map(z => [z.zoneId, { name: z.zoneName, save: true }]))
  );

  /* El candado compartido, con cuenta. Antes guardaba y restauraba su propio
     valor: si este modal se cerraba DESPUÉS del panel que lo abrió, restauraba
     el 'hidden' que había capturado y la app se quedaba sin poder deslizar. */
  useBodyScrollLock();

  const handleConfirm = () => {
    const result: PrivateTemplateSaveDecision[] = zones.map(z => {
      const d = decisions[z.zoneId] || { name: z.zoneName, save: true };
      return {
        zoneId: z.zoneId,
        sourceTemplateId: z.sourceTemplateId,
        name: d.name.trim() || z.zoneName,
        save: d.save && d.name.trim().length > 0,
        items: z.items,
      };
    });
    onConfirm(result);
  };

  const anyToSave = Object.values(decisions).some(d => d.save && d.name.trim().length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/50 animate-in fade-in">
      <div className="bg-white rounded-3xl shadow-s3 w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95">
        {/* Header */}
        <div className="px-6 py-5 border-b border-acero-200 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 grid place-items-center bg-acero-100 text-acero-600 rounded-xl shrink-0">
              <BookmarkPlus size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold tracking-tight text-navy-950 text-lg leading-tight">{t('estimado.plantillas.titulo')}</h2>
              <p className="text-xs text-acero-500 mt-1 leading-relaxed">
                {t('estimado.plantillas.cambiasteprecios')}<span className="font-data tabular-nums">{zones.length}</span> {t('estimado.plantillas.botonplantilla')}{zones.length === 1 ? '' : 's'} in this estimate. {t('estimado.plantillas.guardaconunombre')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-acero-500 hover:text-navy-950 hover:bg-acero-100 rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            aria-label={t('estimado.plantillas.cerrar')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Per-zone list */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 py-4 space-y-3">
          {zones.map(z => {
            const d = decisions[z.zoneId] || { name: z.zoneName, save: true };
            return (
              <div
                key={z.zoneId}
                className={`border rounded-2xl p-4 transition-colors duration-150 ${d.save ? 'border-brand-300' : 'border-acero-200 bg-white'}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="label-ui">{t('estimado.plantillas.basadaen')}</p>
                    <p className="text-sm font-medium text-navy-950 truncate">{z.sourceTemplateName}</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer shrink-0 select-none">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={d.save}
                      onChange={(e) => setDecisions(prev => ({ ...prev, [z.zoneId]: { ...d, save: e.target.checked } }))}
                    />
                    <div className="w-9 h-5 bg-acero-300 peer-checked:bg-navy-850 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-400 rounded-full relative transition-colors duration-150 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
                    <span className="ml-2 text-xs font-medium text-navy-950">{t('estimado.plantillas.guardar')}</span>
                  </label>
                </div>

                <p className="text-[11px] text-acero-500 mb-2">
                  <span className="font-data tabular-nums">{z.itemCount}</span> {t('estimado.plantillas.partidas', { count: z.itemCount })} · <span className="font-data tabular-nums">{z.changedLineCount}</span> changed
                </p>

                {d.save && (
                  <div className="relative">
                    <Pencil size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400 pointer-events-none" />
                    <input
                      type="text"
                      value={d.name}
                      onChange={(e) => setDecisions(prev => ({ ...prev, [z.zoneId]: { ...d, name: e.target.value } }))}
                      placeholder={t('estimado.plantillas.placeholdernombre')}
                      autoFocus={zones.length === 1}
                      className="w-full h-10 pl-9 pr-3 text-sm text-navy-950 bg-white border border-acero-300 rounded-xl placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-acero-200 flex items-center justify-between gap-3 bg-acero-50">
          <Button variant="ghost" onClick={onSkip}>
            {t('estimado.plantillas.omitir')}
          </Button>
          <Button
            variant="secondary"
            onClick={handleConfirm}
            disabled={!anyToSave}
          >
            {anyToSave ? `${t('estimado.plantillas.botonguardar')} ${Object.values(decisions).filter(d => d.save && d.name.trim().length > 0).length} ${t('estimado.plantillas.botonplantilla')}${Object.values(decisions).filter(d => d.save && d.name.trim().length > 0).length === 1 ? '' : 's'}` : t('estimado.plantillas.nadaseleccionado')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveTemplatesPromptModal;
